import { useState, useEffect, useCallback } from 'react';
import { toast } from 'sonner';
import { Plus, Trash2, RefreshCw, Clock, Pencil, X, Check } from 'lucide-react';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { Label } from '@/components/ui/label';
import * as blockService from '@/services/blockService';
import type { BlockDefinition, CreateBlockPayload } from '@/services/blockService';
import { formatPrice } from '@/utils/helpers';

const ROOM_TYPES = [
  { id: 'standard', label: 'Tiêu chuẩn' },
  { id: 'vip', label: 'VIP' },
  { id: 'supervip', label: 'Super VIP' },
] as const;

const EMPTY_FORM: CreateBlockPayload = {
  roomType: 'standard',
  name: '',
  startTime: '09:00',
  endTime: '12:00',
  price: 0,
  sortOrder: 0,
};

export function BlockSettings() {
  const [blocks, setBlocks] = useState<Record<string, BlockDefinition[]>>({});
  const [loading, setLoading] = useState(true);
  const [activeTab, setActiveTab] = useState<string>('standard');
  const [showForm, setShowForm] = useState(false);
  const [editingId, setEditingId] = useState<string | null>(null);
  const [form, setForm] = useState<CreateBlockPayload>({ ...EMPTY_FORM });
  const [saving, setSaving] = useState(false);

  const fetchBlocks = useCallback(async () => {
    try {
      setLoading(true);
      const data = await blockService.getAll();
      setBlocks(data);
    } catch (err) {
      console.error(err);
      toast.error('Không thể tải cấu hình ca đặt phòng');
    } finally {
      setLoading(false);
    }
  }, []);

  useEffect(() => { fetchBlocks(); }, [fetchBlocks]);

  const currentBlocks = blocks[activeTab] || [];

  const handleOpenCreate = () => {
    setEditingId(null);
    setForm({ ...EMPTY_FORM, roomType: activeTab, sortOrder: currentBlocks.length });
    setShowForm(true);
  };

  const handleOpenEdit = (block: BlockDefinition) => {
    setEditingId(block.id);
    setForm({
      roomType: block.roomType,
      name: block.name,
      startTime: block.startTime,
      endTime: block.endTime,
      price: block.price,
      sortOrder: block.sortOrder,
    });
    setShowForm(true);
  };

  const handleCancel = () => {
    setShowForm(false);
    setEditingId(null);
    setForm({ ...EMPTY_FORM });
  };

  const handleSave = async () => {
    const timeRegex = /^([01]\d|2[0-3]):[0-5]\d$/;
    if (!form.name.trim()) { toast.error('Tên ca không được để trống'); return; }
    if (!timeRegex.test(form.startTime)) { toast.error('Giờ bắt đầu không hợp lệ (HH:mm)'); return; }
    if (!timeRegex.test(form.endTime)) { toast.error('Giờ kết thúc không hợp lệ (HH:mm)'); return; }
    if (form.price < 0) { toast.error('Giá phải >= 0'); return; }

    try {
      setSaving(true);
      if (editingId) {
        await blockService.update(editingId, form);
        toast.success(`Đã cập nhật ca "${form.name}"`);
      } else {
        await blockService.create(form);
        toast.success(`Đã tạo ca "${form.name}"`);
      }
      handleCancel();
      fetchBlocks();
    } catch (err: any) {
      toast.error(err.message || 'Lưu thất bại');
    } finally {
      setSaving(false);
    }
  };

  const handleDelete = async (block: BlockDefinition) => {
    if (!confirm(`Xóa ca "${block.name}"?`)) return;
    try {
      await blockService.remove(block.id);
      toast.success(`Đã xóa ca "${block.name}"`);
      fetchBlocks();
    } catch (err: any) {
      toast.error(err.message || 'Xóa thất bại');
    }
  };

  if (loading) {
    return (
      <div className="flex items-center justify-center p-8 bg-card border border-border rounded-xl">
        <RefreshCw className="h-6 w-6 animate-spin text-muted-foreground" />
      </div>
    );
  }

  return (
    <div className="rounded-xl border border-border bg-card p-6 shadow-sm transition-all duration-300 hover:shadow-md">
      {/* Header */}
      <div className="flex items-center justify-between border-b border-border pb-4 mb-6">
        <div>
          <h2 className="text-xl font-bold text-foreground flex items-center gap-2">
            <Clock className="h-5 w-5 text-nhacam-primary" />
            Cấu hình Ca đặt phòng
          </h2>
          <p className="text-sm text-muted-foreground mt-1">
            Định nghĩa các ca cố định (Sáng, Chiều, Tối...) theo từng loại phòng. Giá và khung giờ có thể khác nhau.
          </p>
        </div>
        <Button
          onClick={handleOpenCreate}
          variant="outline"
          size="sm"
          className="flex items-center gap-2 hover:bg-zinc-100 hover:text-black transition-all border-zinc-200"
        >
          <Plus className="h-4 w-4" />
          Thêm ca
        </Button>
      </div>

      {/* Room Type Tabs */}
      <div className="flex gap-1 mb-6 bg-zinc-100 dark:bg-zinc-900 p-1 rounded-lg w-fit">
        {ROOM_TYPES.map(({ id, label }) => (
          <button
            key={id}
            onClick={() => { setActiveTab(id); handleCancel(); }}
            className={`px-4 py-2 text-sm font-medium rounded-md transition-all duration-200 ${
              activeTab === id
                ? 'bg-white dark:bg-zinc-800 text-foreground shadow-sm'
                : 'text-muted-foreground hover:text-foreground'
            }`}
          >
            {label}
            {(blocks[id]?.length ?? 0) > 0 && (
              <span className={`ml-1.5 text-xs px-1.5 py-0.5 rounded-full ${
                activeTab === id
                  ? 'bg-nhacam-primary/10 text-nhacam-primary'
                  : 'bg-zinc-200 dark:bg-zinc-700 text-muted-foreground'
              }`}>
                {blocks[id]?.length}
              </span>
            )}
          </button>
        ))}
      </div>

      {/* Add/Edit Form */}
      {showForm && (
        <div className="mb-6 p-4 bg-zinc-50/50 dark:bg-zinc-900/50 rounded-lg border border-border animate-in fade-in slide-in-from-top-2 duration-200">
          <h3 className="text-sm font-semibold text-foreground mb-3">
            {editingId ? 'Chỉnh sửa ca' : `Thêm ca mới — ${ROOM_TYPES.find(r => r.id === activeTab)?.label}`}
          </h3>
          <div className="grid grid-cols-12 gap-4">
            <div className="col-span-3">
              <Label className="text-xs text-muted-foreground">Tên ca</Label>
              <Input
                value={form.name}
                onChange={(e) => setForm({ ...form, name: e.target.value })}
                placeholder="Ví dụ: Ca sáng"
                className="mt-1"
              />
            </div>
            <div className="col-span-2">
              <Label className="text-xs text-muted-foreground">Bắt đầu</Label>
              <Input
                value={form.startTime}
                onChange={(e) => setForm({ ...form, startTime: e.target.value })}
                placeholder="09:00"
                className="mt-1"
              />
            </div>
            <div className="col-span-2">
              <Label className="text-xs text-muted-foreground">Kết thúc</Label>
              <Input
                value={form.endTime}
                onChange={(e) => setForm({ ...form, endTime: e.target.value })}
                placeholder="12:00"
                className="mt-1"
              />
            </div>
            <div className="col-span-2">
              <Label className="text-xs text-muted-foreground">Giá (VNĐ)</Label>
              <Input
                type="number"
                value={form.price}
                onChange={(e) => setForm({ ...form, price: parseInt(e.target.value) || 0 })}
                className="mt-1"
              />
            </div>
            <div className="col-span-1">
              <Label className="text-xs text-muted-foreground">Thứ tự</Label>
              <Input
                type="number"
                value={form.sortOrder}
                onChange={(e) => setForm({ ...form, sortOrder: parseInt(e.target.value) || 0 })}
                className="mt-1"
              />
            </div>
            <div className="col-span-2 flex items-end gap-2">
              <Button onClick={handleSave} disabled={saving} size="sm" className="bg-zinc-900 text-white hover:bg-zinc-800">
                <Check className="h-4 w-4 mr-1" />
                {saving ? 'Lưu...' : 'Lưu'}
              </Button>
              <Button onClick={handleCancel} variant="ghost" size="sm">
                <X className="h-4 w-4" />
              </Button>
            </div>
          </div>
        </div>
      )}

      {/* Blocks Table */}
      {currentBlocks.length === 0 ? (
        <div className="text-center py-10 text-muted-foreground border border-dashed border-border rounded-lg">
          <Clock className="h-10 w-10 mx-auto mb-3 opacity-30" />
          <p>Chưa có ca nào cho loại phòng {ROOM_TYPES.find(r => r.id === activeTab)?.label}.</p>
          <p className="text-xs mt-1">Nhấp "Thêm ca" để bắt đầu cấu hình.</p>
        </div>
      ) : (
        <div className="space-y-2">
          {/* Table Header */}
          <div className="grid grid-cols-12 gap-4 text-xs font-semibold text-muted-foreground px-4 py-2">
            <div className="col-span-3">Tên ca</div>
            <div className="col-span-2">Giờ bắt đầu</div>
            <div className="col-span-2">Giờ kết thúc</div>
            <div className="col-span-2">Giá</div>
            <div className="col-span-1">Thứ tự</div>
            <div className="col-span-2 text-right">Thao tác</div>
          </div>

          {/* Rows */}
          {currentBlocks.map((block) => (
            <div
              key={block.id}
              className="grid grid-cols-12 gap-4 items-center bg-zinc-50/50 dark:bg-zinc-900/50 px-4 py-3 rounded-lg border border-border transition-all hover:bg-zinc-50 hover:border-zinc-300 dark:hover:bg-zinc-900 duration-200"
            >
              <div className="col-span-3 font-medium text-sm text-foreground">{block.name}</div>
              <div className="col-span-2 text-sm text-muted-foreground font-mono">{block.startTime}</div>
              <div className="col-span-2 text-sm text-muted-foreground font-mono">{block.endTime}</div>
              <div className="col-span-2 text-sm font-semibold text-nhacam-primary">{formatPrice(block.price)}</div>
              <div className="col-span-1 text-sm text-muted-foreground">{block.sortOrder}</div>
              <div className="col-span-2 flex justify-end gap-1">
                <Button onClick={() => handleOpenEdit(block)} variant="ghost" size="icon" className="h-8 w-8 text-muted-foreground hover:text-foreground">
                  <Pencil className="h-3.5 w-3.5" />
                </Button>
                <Button onClick={() => handleDelete(block)} variant="ghost" size="icon" className="h-8 w-8 text-red-500 hover:text-red-700 hover:bg-red-50">
                  <Trash2 className="h-3.5 w-3.5" />
                </Button>
              </div>
            </div>
          ))}
        </div>
      )}
    </div>
  );
}
